<template lang="pug">
.vue-app-time-grid
  FullCalendar.hs-full-calendar(
    ref="fullCalendar"
    :options="calendarOptions"
  )
    template(#dayHeaderContent="{ date }")
      AppTimeGridHeaderTableData(:date="momentDate(date)")
    template(#eventContent="{ event, isMirror }")
      TimeEntriesCalendarEvent(
        v-if="!isMirror"
        :time-zone="timeZone"
        :event="event"
        :store="$store"
        @open-dialog="openDialog"
      )
      span(v-else) {{ formattedEventRange(event.start, event.end) }}
</template>
<script>
import { min, isEmpty } from 'lodash-es';
import FullCalendar from '@fullcalendar/vue3';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import { formatDatetimeRange } from '~/utils/time_utils';
import TimeEntriesCalendarEvent from './time_entries/calendar/TimeEntriesCalendarEvent.vue';

const ResizeObserver = window.ResizeObserver;
const HOUR_TIME_FORMAT_24 = { hour: 'numeric', minute: '2-digit', hour12: false };
let TIME_FORMAT;

export default {
  name: 'AppTimeGrid',
  components: {
    FullCalendar,
    TimeEntriesCalendarEvent,
  },
  props: {
    defaultDate: {
      type: String,
      default: '',
    },
    dates: {
      type: Array,
      default: () => [],
    },
    views: {
      type: Object,
      default() {
        return {
          timeGrid: {
            selectMirror: true,
          },
        };
      },
    },
    plugins: {
      type: Array,
      default: () => [timeGridPlugin, interactionPlugin, momentTimezonePlugin],
    },
    minDate: {
      type: Object,
    },
    events: {
      type: Array,
      default: () => [],
    },
    timeZone: {
      type: String,
      default: '',
    },
    firstDay: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      height: 0,
      resizeObserver: null,
      scrollTime: '08:00:00',
    };
  },
  computed: {
    timeFormat() {
      TIME_FORMAT = /24/.test(TimeUtils.UserTimeFormats.time) ? HOUR_TIME_FORMAT_24 : null;

      return TIME_FORMAT;
    },
    now() {
      const now = moment();

      if (this.timeZone) {
        now.tz(this.timeZone);
      }

      return now;
    },
    calendarOptions() {
      return {
        initialView: 'timeGridWeek',
        allDayText: '',
        eventMinHeight: 25,
        allDaySlot: true,
        editable: false,
        nowIndicator: true,
        slotEventOverlap: false,
        selectable: true,
        headerToolbar: "{ left: '', center: '', right: '' }",
        views: this.views,
        eventClassNames: this.eventClassNames,
        initialDate: this.defaultDate,
        now: this.toDate(this.now),
        timeZone: this.timeZone,
        height: this.height,
        firstDay: this.firstDay,
        plugins: this.plugins,
        events: this.events,
        slotLabelFormat: this.timeFormat,
        eventTimeFormat: this.timeFormat,
        windowResize: this.setHeight,
        select: this.onSelect,
        stickyHeaderDates: true,
        scrollTime: this.scrollTime,
      };
    },
  },
  created() {
    this.setScrollTime();
  },
  mounted() {
    this.setupResizeObserver();
  },
  beforeUnmount() {
    this.stopResizeObserver();
  },
  methods: {
    momentDate(date) {
      return this.timeZone ? moment(date).tz(this.timeZone) : moment(date);
    },
    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(this.onResize);
      });
      this.resizeObserver.observe(this.$el);
    },
    stopResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    },
    isFuture(date) {
      return date.isAfter(this.now);
    },
    isBefore(date) {
      return date.isBefore(this.minDate, 'day');
    },
    setScrollTime() {
      const format = 'HH:mm:ss';
      const hourlyEvents = this.events.filter((event) => {
        // check if event is today, otherwise scroll to the current time of the day
        return new Date().toDateString() === event.start.toDateString();
      });

      if (!isEmpty(hourlyEvents)) {
        const starts = hourlyEvents.map((event) => moment(event.start).tz(this.timeZone));
        const datetime = starts[0];
        const startsAsSameDay = starts.map((time) =>
          time.set({ year: datetime.year(), month: datetime.month(), date: datetime.date() }),
        );
        const start = min(startsAsSameDay);

        this.scrollTime = moment(start).format(format);
      } else if (this.dates.find((date) => date.isSame(this.now, 'day'))) {
        // add 5 minutes gap for the scroll time for event or calendar arrow to be visible
        this.scrollTime = moment(this.now).subtract(5, 'minutes').format(format);
      }
    },
    onResize() {
      this.setHeight();
    },
    toDate(date) {
      if (moment.isMoment(date)) {
        return date.toDate();
      }

      return date;
    },
    eventClassNames(info) {
      const start = moment(info.event.start);
      const end = moment(info.event.end);
      const duration = end - start;
      const classNames = [];

      if (info.isMirror) {
        const oneDay = moment.duration(1, 'day');
        const greaterThanOneDay = duration - oneDay > 0;

        if (this.isFuture(start) || this.isFuture(end) || this.isBefore(start) || greaterThanOneDay) {
          classNames.push('hide');
        }
      } else {
        const lessThanHour = duration < moment.duration(1, 'hour').asMilliseconds();
        const event = info.event;

        if (event.extendedProps.isRequest) {
          classNames.push('is-request');
        }
        if (lessThanHour) {
          classNames.push('fc-short');
        }
        if (event.allDay) {
          classNames.push('fc-all-day');
        }
      }

      return classNames;
    },
    onSelect(payload) {
      this.$emit('date-select', payload);
    },
    setHeight() {
      this.height = $(window).height() - this.$el.offsetTop;
    },
    openDialog(event) {
      this.$emit('open-dialog', event);
    },
    formattedEventRange(start, stop) {
      return formatDatetimeRange(moment(start), moment(stop), { style: 'time' });
    },
  },
};
</script>
